import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { auth } from '../../firebase/firebase';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
 
const C = {
    primary:          '#6a37d4',
    primaryLight:     '#ae8dff',
    primaryContainer: '#ede8ff',
    secondary:        '#006947',
    secondaryContainer: '#d1fae5',
    secondaryLight:   '#34d399',
    surface:          '#f3f6ff',
    surfaceLow:       '#eaf1ff',
    surfaceHigh:      '#d6e3f9',
    onBackground:     '#252f3c',
    onSurface:        '#252f3c',
    onSurfaceVariant: '#525c6a',
    outlineVariant:   '#a4aebe',
};
 
const SignupEducator: React.FC = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState<'info' | 'avatar' | 'verification'>('info');
    const [username, setUsername]             = useState('');
    const [email, setEmail]                   = useState('');
    const [password, setPassword]             = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [educatorType, setEducatorType]     = useState<'teacher' | 'librarian' | 'instructor' | ''>('');
    const [schoolName, setSchoolName]         = useState('');
    const [selectedAvatar, setSelectedAvatar] = useState('');
    const [error, setError]                   = useState('');
    const [loading, setLoading]               = useState(false);
 
    const avatars = ['🐱', '🐶', '🦊', '🐼', '🦁', '🐸', '🦄', '🐉', '🦖', '🚀', '⭐', '🌈'];
 
    const educatorTypes = [
        { id: 'teacher',    emoji: '👩‍🏫', label: 'Teacher',    desc: 'English, Writing, Literature' },
        { id: 'librarian',  emoji: '📚',   label: 'Librarian',  desc: 'School or public library' },
        { id: 'instructor', emoji: '🎓',   label: 'Instructor', desc: 'Writing coach, tutor, workshop leader' },
    ];
 
    const steps = ['info', 'avatar', 'verification'];
 
    const isEducationalEmail = (email: string): boolean => {
        const domains = ['.edu', '.k12.us', '.school.', '.ac.uk', '.edu.au'];
        return domains.some(d => email.toLowerCase().includes(d));
    };
 
    // ── Handlers (logic unchanged) ────────────────────────────────────────────
    const handleInfoSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        if (!username || username.length < 3)  { setError('Username must be at least 3 characters'); return; }
        if (!email)                             { setError('Please enter your email'); return; }
        if (!isEducationalEmail(email))         { setError('Please use a school or educational email (.edu, .k12.us, etc.)'); return; }
        if (password.length < 6)               { setError('Password must be at least 6 characters'); return; }
        if (password !== confirmPassword)       { setError('Passwords do not match'); return; }
        if (!educatorType)                      { setError('Please select your role'); return; }
        if (!schoolName.trim())                 { setError('Please enter your school or institution name'); return; }
        setStep('avatar');
    };
 
    const handleAvatarSubmit = () => {
        if (!selectedAvatar) { setError('Please choose an avatar'); return; }
        setError('');
        setStep('verification');
    };
 
    const handleVerificationSubmit = async () => {
        setLoading(true);
        try {
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            await updateProfile(userCredential.user, { displayName: username });
            await setDoc(doc(db, 'users', userCredential.user.uid), {
                uid: userCredential.user.uid,
                displayName: username,
                email,
                role: 'educator',
                educatorType,
                schoolName: schoolName.trim(),
                classes: [],
                avatar: selectedAvatar,
                createdAt: serverTimestamp(),
                verified: false,
                verificationDate: null,
            });
            navigate('/educator-dashboard');
        } catch (err: any) {
            setError(err.message || 'Failed to create account');
        } finally {
            setLoading(false);
        }
    };
 
    // ── Shared styles ─────────────────────────────────────────────────────────
    const inputStyle: React.CSSProperties = {
        width: '100%', padding: '11px 14px',
        border: `1.5px solid ${C.outlineVariant}`,
        borderRadius: '10px', fontSize: '0.95rem',
        boxSizing: 'border-box',
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        background: C.surface, color: C.onSurface,
        outline: 'none', transition: 'border-color 0.15s',
    };
 
    const labelStyle: React.CSSProperties = {
        display: 'block', marginBottom: '6px',
        fontSize: '0.8rem', fontWeight: '700',
        color: C.onSurfaceVariant,
        fontFamily: "'Epilogue', sans-serif",
    };
 
    // Educator flow uses secondary (green) as accent
    const primaryBtnStyle: React.CSSProperties = {
        width: '100%', padding: '14px',
        background: `linear-gradient(135deg, ${C.secondary} 0%, ${C.secondaryLight} 100%)`,
        color: '#fff', border: 'none', borderRadius: '9999px',
        fontSize: '1rem', fontWeight: '700',
        cursor: 'pointer', marginBottom: '12px',
        fontFamily: "'Epilogue', sans-serif",
        boxShadow: `0 4px 14px ${C.secondary}40`,
        transition: 'opacity 0.15s, transform 0.15s',
    };
 
    const ghostBtnStyle: React.CSSProperties = {
        width: '100%', padding: '12px',
        background: 'transparent',
        color: C.onSurfaceVariant,
        border: `1.5px solid ${C.outlineVariant}`,
        borderRadius: '9999px',
        fontSize: '0.9rem', fontWeight: '700',
        cursor: 'pointer',
        fontFamily: "'Epilogue', sans-serif",
        transition: 'border-color 0.15s, color 0.15s',
    };
 
    return (
        <div style={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            background: C.surface, minHeight: '100vh',
            padding: '40px 20px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
            <div style={{
                background: '#fff', borderRadius: '24px',
                padding: '40px 36px', maxWidth: '520px', width: '100%',
                boxShadow: '0 20px 60px rgba(0,0,0,0.1)',
                border: `1px solid ${C.surfaceHigh}`,
            }}>
                {/* Progress bar */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '32px' }}>
                    {steps.map((s, i) => (
                        <div key={s} style={{
                            height: '5px', flex: 1, borderRadius: '9999px',
                            background: steps.indexOf(step) >= i ? C.secondary : C.surfaceHigh,
                            transition: 'background 0.3s ease',
                        }} />
                    ))}
                </div>
 
                {/* Heading */}
                <h1 style={{
                    fontFamily: "'Epilogue', sans-serif",
                    fontSize: '1.8rem', fontWeight: '900',
                    color: C.onBackground, margin: '0 0 6px 0', textAlign: 'center',
                }}>
                    {step === 'info'         && '🎓 Educator Sign Up'}
                    {step === 'avatar'       && 'Choose Your Avatar'}
                    {step === 'verification' && 'Ready to Go!'}
                </h1>
 
                {step === 'info' && (
                    <p style={{ textAlign: 'center', fontSize: '0.9rem', color: C.onSurfaceVariant, margin: '0 0 24px 0' }}>
                        Create classes, assign writing, inspire your students
                    </p>
                )}
 
                {/* Error */}
                {error && (
                    <div style={{
                        background: '#fef2f2', border: '1px solid #fecaca',
                        borderRadius: '10px', padding: '12px',
                        marginBottom: '18px', color: '#dc2626',
                        fontSize: '0.875rem', textAlign: 'center', fontWeight: '500',
                    }}>
                        {error}
                    </div>
                )}
 
                {/* ── Step 1: Info ──────────────────────────────────────────── */}
                {step === 'info' && (
                    <form onSubmit={handleInfoSubmit}>
                        {/* Name */}
                        <div style={{ marginBottom: '16px' }}>
                            <label style={labelStyle}>Your Name</label>
                            <input
                                type="text" value={username}
                                onChange={e => setUsername(e.target.value)}
                                placeholder="Your name or preferred name" required
                                style={inputStyle}
                                onFocus={e => { e.target.style.borderColor = C.secondary; }}
                                onBlur={e  => { e.target.style.borderColor = C.outlineVariant; }}
                            />
                        </div>
 
                        {/* Email */}
                        <div style={{ marginBottom: '16px' }}>
                            <label style={labelStyle}>School / Institution Email</label>
                            <input
                                type="email" value={email}
                                onChange={e => setEmail(e.target.value)}
                                placeholder="yourname@school.edu" required
                                style={inputStyle}
                                onFocus={e => { e.target.style.borderColor = C.secondary; }}
                                onBlur={e  => { e.target.style.borderColor = C.outlineVariant; }}
                            />
                            <div style={{
                                marginTop: '8px', background: C.secondaryContainer,
                                border: `1.5px solid ${C.secondary}30`,
                                borderRadius: '10px', padding: '10px 12px',
                                fontSize: '0.8rem', color: C.secondary, fontWeight: '600',
                            }}>
                                ✅ Use your school or institution email (.edu, .k12.us, etc.)
                            </div>
                        </div>
 
                        {/* School name */}
                        <div style={{ marginBottom: '16px' }}>
                            <label style={labelStyle}>School / Institution Name</label>
                            <input
                                type="text" value={schoolName}
                                onChange={e => setSchoolName(e.target.value)}
                                placeholder="e.g., Lincoln High School" required
                                style={inputStyle}
                                onFocus={e => { e.target.style.borderColor = C.secondary; }}
                                onBlur={e  => { e.target.style.borderColor = C.outlineVariant; }}
                            />
                        </div>
 
                        {/* Role picker */}
                        <div style={{ marginBottom: '16px' }}>
                            <label style={labelStyle}>Your Role</label>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {educatorTypes.map(type => (
                                    <button
                                        key={type.id}
                                        type="button"
                                        onClick={() => setEducatorType(type.id as any)}
                                        style={{
                                            padding: '12px 16px',
                                            border: `2px solid ${educatorType === type.id ? C.secondary : C.outlineVariant}`,
                                            borderRadius: '12px',
                                            background: educatorType === type.id ? C.secondaryContainer : '#fff',
                                            cursor: 'pointer', textAlign: 'left',
                                            display: 'flex', alignItems: 'center', gap: '12px',
                                            transition: 'all 0.15s',
                                        }}
                                        onMouseEnter={e => { if (educatorType !== type.id) e.currentTarget.style.borderColor = C.secondary; }}
                                        onMouseLeave={e => { if (educatorType !== type.id) e.currentTarget.style.borderColor = C.outlineVariant; }}
                                    >
                                        <span style={{ fontSize: '1.5rem', flexShrink: 0 }}>{type.emoji}</span>
                                        <div>
                                            <div style={{
                                                fontFamily: "'Epilogue', sans-serif",
                                                fontWeight: '800', fontSize: '0.9rem',
                                                color: educatorType === type.id ? C.secondary : C.onBackground,
                                                marginBottom: '1px',
                                            }}>
                                                {type.label}
                                            </div>
                                            <div style={{ fontSize: '0.78rem', color: C.onSurfaceVariant }}>
                                                {type.desc}
                                            </div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
 
                        {/* Password */}
                        <div style={{ marginBottom: '16px' }}>
                            <label style={labelStyle}>Password</label>
                            <input
                                type="password" value={password}
                                onChange={e => setPassword(e.target.value)}
                                placeholder="Create a strong password" required
                                style={inputStyle}
                                onFocus={e => { e.target.style.borderColor = C.secondary; }}
                                onBlur={e  => { e.target.style.borderColor = C.outlineVariant; }}
                            />
                        </div>
 
                        {/* Confirm password */}
                        <div style={{ marginBottom: '24px' }}>
                            <label style={labelStyle}>Confirm Password</label>
                            <input
                                type="password" value={confirmPassword}
                                onChange={e => setConfirmPassword(e.target.value)}
                                placeholder="Type password again" required
                                style={inputStyle}
                                onFocus={e => { e.target.style.borderColor = C.secondary; }}
                                onBlur={e  => { e.target.style.borderColor = C.outlineVariant; }}
                            />
                        </div>
 
                        <button
                            type="submit"
                            style={primaryBtnStyle}
                            onMouseEnter={e => { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                            onMouseLeave={e => { e.currentTarget.style.opacity = '1';   e.currentTarget.style.transform = 'translateY(0)'; }}
                        >
                            Next: Choose Avatar →
                        </button>
 
                        <div style={{ textAlign: 'center', fontSize: '0.85rem', color: C.onSurfaceVariant }}>
                            Already have an account?{' '}
                            <button
                                type="button"
                                onClick={() => navigate('/login')}
                                style={{
                                    background: 'none', border: 'none',
                                    color: C.secondary, fontWeight: '700',
                                    cursor: 'pointer', fontSize: '0.85rem',
                                    fontFamily: "'Epilogue', sans-serif", padding: 0,
                                }}
                            >
                                Sign In
                            </button>
                        </div>
                    </form>
                )}
 
                {/* ── Step 2: Avatar ───────────────────────────────────────── */}
                {step === 'avatar' && (
                    <div>
                        <p style={{ textAlign: 'center', fontSize: '0.9rem', color: C.onSurfaceVariant, margin: '0 0 20px 0' }}>
                            Pick an avatar that represents you!
                        </p>
 
                        <div style={{
                            display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
                            gap: '10px', marginBottom: '24px',
                        }}>
                            {avatars.map(avatar => (
                                <button
                                    key={avatar}
                                    type="button"
                                    onClick={() => { setSelectedAvatar(avatar); setError(''); }}
                                    style={{
                                        fontSize: '2rem', padding: '16px',
                                        border: `2px solid ${selectedAvatar === avatar ? C.secondary : C.outlineVariant}`,
                                        borderRadius: '14px',
                                        background: selectedAvatar === avatar ? C.secondaryContainer : '#fff',
                                        cursor: 'pointer', transition: 'all 0.15s',
                                    }}
                                    onMouseEnter={e => { if (selectedAvatar !== avatar) e.currentTarget.style.borderColor = C.secondary; }}
                                    onMouseLeave={e => { if (selectedAvatar !== avatar) e.currentTarget.style.borderColor = C.outlineVariant; }}
                                >
                                    {avatar}
                                </button>
                            ))}
                        </div>
 
                        <button
                            onClick={handleAvatarSubmit}
                            style={primaryBtnStyle}
                            onMouseEnter={e => { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                            onMouseLeave={e => { e.currentTarget.style.opacity = '1';   e.currentTarget.style.transform = 'translateY(0)'; }}
                        >
                            Next: Review & Finish →
                        </button>
 
                        <button
                            onClick={() => setStep('info')}
                            style={ghostBtnStyle}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = C.secondary; e.currentTarget.style.color = C.secondary; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = C.outlineVariant; e.currentTarget.style.color = C.onSurfaceVariant; }}
                        >
                            ← Back
                        </button>
                    </div>
                )}
 
                {/* ── Step 3: Review & confirm ─────────────────────────────── */}
                {step === 'verification' && (
                    <div>
                        <p style={{ textAlign: 'center', fontSize: '0.9rem', color: C.onSurfaceVariant, margin: '0 0 20px 0' }}>
                            You're all set! Review your details and create your account.
                        </p>
 
                        {/* Summary card */}
                        <div style={{
                            background: C.secondaryContainer,
                            border: `1.5px solid ${C.secondary}30`,
                            borderRadius: '16px', padding: '24px',
                            marginBottom: '24px', textAlign: 'center',
                        }}>
                            <div style={{ fontSize: '3rem', marginBottom: '10px' }}>{selectedAvatar}</div>
                            <div style={{
                                fontFamily: "'Epilogue', sans-serif",
                                fontSize: '1.1rem', fontWeight: '900',
                                color: C.onBackground, marginBottom: '4px',
                            }}>
                                {username}
                            </div>
                            <div style={{ fontSize: '0.85rem', color: C.onSurfaceVariant, marginBottom: '8px' }}>
                                {email}
                            </div>
                            <div style={{
                                display: 'inline-flex', alignItems: 'center', gap: '6px',
                                background: '#fff', borderRadius: '9999px',
                                padding: '4px 14px', fontSize: '0.82rem',
                                fontWeight: '700', color: C.secondary,
                            }}>
                                {educatorTypes.find(t => t.id === educatorType)?.emoji}{' '}
                                {educatorTypes.find(t => t.id === educatorType)?.label} · {schoolName}
                            </div>
                        </div>
 
                        <button
                            onClick={handleVerificationSubmit}
                            disabled={loading}
                            style={{
                                ...primaryBtnStyle,
                                background: loading
                                    ? C.outlineVariant
                                    : `linear-gradient(135deg, ${C.secondary} 0%, ${C.secondaryLight} 100%)`,
                                cursor: loading ? 'not-allowed' : 'pointer',
                                boxShadow: loading ? 'none' : `0 4px 14px ${C.secondary}40`,
                            }}
                            onMouseEnter={e => { if (!loading) { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.transform = 'translateY(-1px)'; } }}
                            onMouseLeave={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'translateY(0)'; }}
                        >
                            {loading ? 'Creating Account…' : 'Create Educator Account 🎉'}
                        </button>
 
                        <button
                            onClick={() => setStep('avatar')}
                            disabled={loading}
                            style={{ ...ghostBtnStyle, cursor: loading ? 'not-allowed' : 'pointer' }}
                            onMouseEnter={e => { if (!loading) { e.currentTarget.style.borderColor = C.secondary; e.currentTarget.style.color = C.secondary; } }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = C.outlineVariant; e.currentTarget.style.color = C.onSurfaceVariant; }}
                        >
                            ← Back
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
 
export default SignupEducator;